
import { supabase } from "@/integrations/supabase/client";

// Constants for ElevenLabs agent
export const AGENT_ID = "reheat-agent"; // Replace with your actual Reheat Agent ID

// Get available agents from ElevenLabs
export const fetchAvailableAgents = async (): Promise<{ success: boolean; agents?: any[]; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke("list-elevenlabs-agents", {
      body: {},
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Failed to fetch available agents");

    return { 
      success: true, 
      agents: data.agents
    };
  } catch (error) {
    console.error("Error fetching available agents:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

// Get details about a specific agent
export const getAgentDetails = async (
  agentId: string = AGENT_ID
): Promise<{ success: boolean; agent?: any; error?: string }> => {
  try {
    console.log(`Fetching details for agent: ${agentId}`);
    
    const { data, error } = await supabase.functions.invoke("get-elevenlabs-agent", {
      body: { agentId },
    });

    if (error) {
      console.error("Supabase function error:", error);
      throw error;
    }
    
    if (!data.success) {
      console.error("ElevenLabs API error:", data.error);
      throw new Error(data.error || "Failed to get agent details");
    }

    return { 
      success: true, 
      agent: data.agent
    };
  } catch (error) {
    console.error("Error fetching agent details:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

// Update an ElevenLabs agent - improved with better error handling and logging
export const updateElevenLabsAgent = async (
  agentId: string,
  data: {
    name?: string;
    description?: string;
    prompt?: string;
    voice_id?: string;
    first_message?: string;
    language?: string;
    max_duration_seconds?: number;
    tts_settings?: {
      stability?: number;
      similarity_boost?: number;
      speed?: number;
      optimize_streaming_latency?: number;
    };
  }
): Promise<{ success: boolean; agent?: any; error?: string; details?: any }> => {
  try {
    console.log(`Updating ElevenLabs agent: ${agentId} with data:`, JSON.stringify(data, null, 2));
    
    // First verify that agent exists to catch issues early
    const checkResult = await getAgentDetails(agentId);
    if (!checkResult.success) {
      console.error("Failed agent existence check:", checkResult.error);
      throw new Error(`Agent does not exist or cannot be accessed: ${checkResult.error}`);
    }
    
    const { data: response, error } = await supabase.functions.invoke("update-elevenlabs-agent", {
      body: { 
        agentId,
        ...data
      },
    });

    if (error) {
      console.error("Supabase function error:", error);
      throw error;
    }
    
    if (!response.success) {
      console.error("ElevenLabs update failed:", response.error);
      throw new Error(response.error || "Failed to update agent");
    }

    console.log("ElevenLabs agent update successful:", response.agent?.name);
    
    return { 
      success: true, 
      agent: response.agent
    };
  } catch (error) {
    console.error("Error updating ElevenLabs agent:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred",
      details: error
    };
  }
};
